import { GridInitialState, GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useError } from "../../contexts/ErrorContext";
import useApi from "../../hooks/useGaneshApi";
import GaneshDataGrid, { GaneshDataGridProps } from "./GaneshDataGrid";

interface GaneshDataGridServerProcessingProps extends GaneshDataGridProps {
  fetchApi: string;
  fetchParams?: any;
  defaultGridState?: GridInitialState;
  rowTransformer?: (rows: any[]) => any[];
  initialSortingModel?: { field: string; sort: string }[];
}

const defaultGridStateServerProcessing: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "id", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 250, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

export interface GaneshDataGridFilterItem {
  id?: number;
  field: string;
  operator: string;
  value: string | number | boolean | null;
}

const GaneshDataGridServerProcessing: React.FC<
  GaneshDataGridServerProcessingProps
> = (props) => {
  const {
    gridId,
    fetchApi,
    columns,
    defaultGridState = defaultGridStateServerProcessing,
    initialState,
    ...otherProps
  } = props;

  const [serverProcessedData, setServerProcessedData] = useState<any[]>([]);
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>(
    (defaultGridState.pagination?.paginationModel || {
      pageSize: 250,
      page: 0,
    }) as {
      pageSize: number;
      page: number;
    }
  );

  const [filterModel, setFilterModel] = useState<{
    items: any[];
  }>(defaultGridState.filter?.filterModel || { items: [] });
  const [filterLogicOperator, setFilterLogicOperator] = useState<string>("and");
  const [sortingModel, setSortingModel] = useState<
    { field: string; sort: string }[]
  >(() =>
    (props.initialSortingModel || defaultGridState.sorting?.sortModel || [{ field: "id", sort: "asc" }]).map(
      ({ field, sort }) => ({
        field,
        sort: sort || "asc",
      })
    )
  );

  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const { showError } = useError();
  const { apiRequest } = useApi();

  const fetchServerData = useCallback(
    async (
      currentFilterModel: { items: GaneshDataGridFilterItem[] } = filterModel
    ) => {
      setLoading(true);
      try {
        const combinedParams = {
          ...props.fetchParams,
          pageSize: paginationModel.pageSize.toString(),
          page: paginationModel.page.toString(),
          filters: JSON.stringify(
            currentFilterModel.items.map(
              ({ field, operator, value }: GaneshDataGridFilterItem) => ({
                field,
                operator,
                value,
              })
            )
          ),
          filterLogic: filterLogicOperator,
          sort: JSON.stringify(
            sortingModel.map(({ field, sort }) => ({ field, sort }))
          ),
        };
        const params = new URLSearchParams(
          combinedParams as Record<string, string>
        );
        const response = await apiRequest<any>(
          `${fetchApi}?${params.toString()}`,
          { method: "GET" }
        );

        if (response.data) {
          // Handle both old and new response formats
          let rows;
          let totalRowCount;
          
          // Check if this is the new success_response format
          if (response.data.data && response.data.data.data) {
            // New format: response.data.data.data
            rows = response.data.data.data;
            totalRowCount = response.data.data.totalRowCount;
          } else if (response.data.data) {
            // Old format: response.data.data
            rows = response.data.data;
            totalRowCount = response.data.totalRowCount;
          } else {
            // Fallback: response.data
            rows = response.data;
            totalRowCount = response.data.totalRowCount || 0;
          }
          
          if (rows && props.rowTransformer) {
            rows = props.rowTransformer(rows);
          }
          
          setServerProcessedData(rows || []);
          setRowCount(totalRowCount || 0);
        }
      } catch (error) {
        console.error(error);
        showError(`Failed to fetch for ${gridId}.`);
      } finally {
        setLoading(false);
      }
    },
    [
      paginationModel,
      sortingModel,
      filterLogicOperator,
      props.fetchParams,
      apiRequest,
      showError,
    ]
  );

  const debouncedFetchFilteredData = useMemo(
    () => debounce((model) => fetchServerData(model), 250),
    [fetchServerData]
  );
  useEffect(() => {
    fetchServerData();
  }, [paginationModel, sortingModel]);

  const handlePaginationChange = (newPagination: {
    pageSize: number;
    page: number;
  }) => {
    setPaginationModel(newPagination);
  };

  const handleFilterChange = (newFilterModel: any) => {
    setFilterLogicOperator(newFilterModel.logicOperator || "and");
    setFilterModel(newFilterModel);
    debouncedFetchFilteredData(newFilterModel);
  };

  const handleSortModelChange = (newSortModel: any) => {
    setSortingModel(newSortModel);
  };

  return (
    <GaneshDataGrid
      {...otherProps}
      gridId={gridId}
      loading={loading}
      data={serverProcessedData}
      columns={columns}
      rowCount={rowCount}
      defaultGridState={defaultGridState}
      initialState={initialState}
      paginationMode="server"
      onPaginationModelChange={handlePaginationChange}
      filterMode="server"
      onFilterModelChange={handleFilterChange}
      sortingMode="server"
      onSortModelChange={handleSortModelChange}
    />
  );
};

export default GaneshDataGridServerProcessing;
